import React, {useEffect, useState} from 'react';
import axiosInstance from "../../axios/axiosInstance";
import {Form, Input, message, Modal, Select, Spin} from "antd";
import { useTranslation } from 'react-i18next';
import {ArrowUturnLeftIcon, PlusIcon} from "@heroicons/react/24/outline";
import {
   ActionButton,
   DataTable,
   PageCard,
   PageShell,
   PageToolbar,
   StatusBadge,
} from "../../components/ui";

const statusTones = {
   process: 'blue',
   accept: 'green',
   cancel: 'red',
};

function DoctorRequest(props) {
   const { t } = useTranslation();
   const [requests, setRequests] = useState([])
   const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
   const [isReturnModalVisible, setIsReturnModalVisible] = useState(false);
   const [warehouses, setWarehouses] = useState([]);
   const [form] = Form.useForm();
   const [loading, setLoading] = useState(false);
   const [loadingRequest, setLoadingRequest] = useState(false);
   const [selectedRequest, setSelectedRequest] = useState(null);
   const [isModalVisible, setIsModalVisible] = useState(false);
   const [isDescriptionModalVisible, setIsDescriptionModalVisible] = useState(false);
   const [fullDescription, setFullDescription] = useState('');
   const [returnForm] = Form.useForm();
   const [selectedWarehouse, setSelectedWarehouse] = useState(null);
   const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 20,
      total: 0,
   });

   const qtyValidator = (_, value) => {
      if (value && value > 100) {
         return Promise.reject(new Error(t('store.qtyMax')));
      }
      return Promise.resolve();
   };

   const openReturnModal = (request) => {
      setSelectedRequest(request);
      setIsReturnModalVisible(true);
   };

   const closeReturnModal = () => {
      setIsReturnModalVisible(false);
      setSelectedRequest(null);
      returnForm.resetFields();
   };

   const openModal = (request) => {
      setSelectedRequest(request);
      setIsModalVisible(true);
   };

   const closeModal = () => {
      setIsModalVisible(false);
      setSelectedRequest(null);
   };

   const handleReturnRequest = async (values) => {
      try {
         const payload = {
            application_id: selectedRequest.id,
            qty: values.qty,
            comment: values.comment || null,
         };
         await axiosInstance.post('/doctor/return_warehouses', payload);
         message.success(t('store.returnOk'));
         closeReturnModal();
         getRequests();
      } catch (error) {
         console.error('Error returning product:', error);
         message.error(t('store.returnError'));
      }
   };

   const getRequests = async (page = 1) => {
      setLoading(true);
      try {
         const response = await axiosInstance.get(`/doctor/application/paginate?page=${page}`);
         const { data, meta } = response.data;
         setRequests(data);
         setPagination({
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
         });
      } catch (error) {
         console.error("Error fetching requests:", error);
         message.error(t('store.fetchDataError'));
      } finally {
         setLoading(false);
      }
   };

   // Fetch available warehouses for the create modal
   const fetchWarehouses = async () => {
      try {
         const response = await axiosInstance.get('/warehouse/all');
         setWarehouses(response.data.data);
      } catch (error) {
         console.error('Error fetching warehouses:', error);
         message.error(t('store.fetchWarehousesError'));
      }
   };

   useEffect(() => {
      getRequests();
      fetchWarehouses();
   }, []);

   const renderStatusTag = (status) => (
       <StatusBadge tone={statusTones[status] ?? 'muted'}>{t(`status.${status}`, { defaultValue: t('common.unknown') })}</StatusBadge>
   );

   const linkButton = (label, onClick) => (
       <button
           type="button"
           onClick={onClick}
           className="text-left text-sm font-medium text-[#1D2C7B] hover:underline"
       >
          {label}
       </button>
   );

   const openCreateModal = () => {
      setIsCreateModalVisible(true);
   };

   const closeCreateModal = () => {
      setIsCreateModalVisible(false);
      form.resetFields();
   };

   const openDescriptionModal = (desc) => {
      setFullDescription(desc);
      setIsDescriptionModalVisible(true);
   };

   const closeDescriptionModal = () => {
      setIsDescriptionModalVisible(false);
      setFullDescription('');
   };

    const handleCreateRequest = async (values) => {
        try {
            setLoading(true); // Start loading
            await axiosInstance.post('/doctor/applications', values);
            message.success(t('store.createOk'));
            setIsCreateModalVisible(false);
            form.resetFields();
            getRequests();
        } catch (error) {
            console.error('Error creating request:', error);
            message.error(t('store.createError'));
        } finally {
            setLoading(false); // Stop loading
        }
    };

   const returnButton = (record) => {
      if (record.status === 'cancel' || record.qty === 0 || record.status === 'process') {
         return null; // Do not render the button
      }
      return (
          <button
              type="button"
              onClick={() => openReturnModal(record)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-[#1D2C7B] hover:bg-slate-50 transition-colors"
          >
             <ArrowUturnLeftIcon className="w-4 h-4"/>
             {t('store.returnGoods')}
          </button>
      );
   };

   const commentCell = (warehouse, record) => {
      const history = warehouse.histories.find((hist) => hist.id === record.id);
      const comment = history?.history?.comment;
      return comment && comment.length > 10 ? (
          linkButton(`${comment.substring(0, 10)}...`, () => openDescriptionModal(comment))
      ) : (
          <span className="text-sm text-slate-700">{comment || t('store.noRemark')}</span>
      );
   };

   const columns = [
      {
         title: t('common.id'),
         dataIndex: 'id',
         key: 'id',
         width: 72,
         render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
      },
      {
         title: t('common.qty'),
         dataIndex: 'qty',
         key: 'qty',
         width: 90,
         render: (qty) => <span className="text-sm text-slate-700 tabular-nums">{qty}</span>,
      },
      {
         title: t('common.date'),
         dataIndex: 'created_at',
         key: 'created_at',
         width: 170,
         render: (value) => <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{value}</span>,
      },
      {
         title: t('store.warehouse'),
         dataIndex: 'warehouse',
         key: 'warehouse',
         render: (warehouse) => linkButton(warehouse.name, () => openModal(warehouse))
      },
      {
         title: t('store.confirmer'),
         dataIndex: 'receive',
         key: 'receive',
         width: 180,
         render: (receive) => (
             receive
                 ? linkButton(receive.name, () => openModal(receive))
                 : <StatusBadge tone="muted">{t('store.notConfirmed')}</StatusBadge>
         )
      },
      {
         title: t('common.status'),
         dataIndex: 'status',
         key: 'status',
         width: 170,
         render: (status) => renderStatusTag(status)
      },
      {
         title: t('store.comment'),
         dataIndex: 'warehouse',
         key: 'comment',
         width: 130,
         render: (warehouse, record) => commentCell(warehouse, record)
      },

      {
         title: t('common.actions'),
         key: 'actions',
         width: 190,
         render: (text, record) => returnButton(record),
      },

   ];

   const renderMobileCard = (record) => {
      const action = returnButton(record);

      return (
          <div>
             <div className="min-w-0">
                {linkButton(record.warehouse?.name, () => openModal(record.warehouse))}
                <span className="block text-xs text-slate-500 tabular-nums mt-0.5">ID: {record.id}</span>
             </div>

             <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                {renderStatusTag(record.status)}
                <span className="tabular-nums">{record.qty}</span>
                <span className="tabular-nums">{record.created_at}</span>
             </div>

             {action && <div className="mt-2.5">{action}</div>}
          </div>
      );
   };

   const handleTableChange = (pagination) => {
      getRequests(pagination.current);
   };

   return (
       <PageShell>
          <PageCard>
             <PageToolbar
                 title={t('store.doctorRequests')}
                 count={pagination.total}
                 actions={
                    <ActionButton icon={<PlusIcon className="w-4 h-4"/>} onClick={openCreateModal}>
                       {t('store.newRequest')}
                    </ActionButton>
                 }
             />

             <DataTable
                 columns={columns}
                 dataSource={requests}
                 rowKey="id"
                 scrollX={1200}
                 mobileCard={renderMobileCard}
                 pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    position: ['bottomCenter'],
                    className: 'px-4 pb-3',
                 }}
                 rowClassName={(record) => (record.qty === 0 ? 'bg-gray-50' : '')}
                 loading={loading}
                 onChange={handleTableChange}
             />
          </PageCard>

          <Modal
              title={t('store.returnGoods')}
              open={isReturnModalVisible}
              onCancel={closeReturnModal}
              onOk={() => returnForm.submit()}
              okText={t('store.send')}
              width="100%"
              style={{maxWidth: 520}}
          >
             <Form form={returnForm} onFinish={handleReturnRequest} layout="vertical">
                <Form.Item
                    label={t('store.qtyLabel', { qty: selectedRequest?.qty })}
                    name="qty"
                    rules={[
                       { required: true, message: t('store.qtyRequired') },
                       {
                          validator: (_, value) => {
                             if (!value || value <= selectedRequest?.qty) {
                                return Promise.resolve();
                             }
                             return Promise.reject(
                                 new Error(t('store.qtyExceed', { qty: selectedRequest?.qty }))
                             );
                          },
                       },
                    ]}
                >
                   <Input
                       type="number"
                       placeholder={t('store.qtyPhInput')}
                       max={selectedRequest?.qty}
                   />
                </Form.Item>


                <Form.Item label={t('common.remark')} name="comment">
                   <Input.TextArea placeholder={t('store.commentOptional')} />
                </Form.Item>
             </Form>
          </Modal>

          {/* Modal for showing details */}
          <Modal
              title={t('store.details')}
              open={isModalVisible}
              onCancel={closeModal}
              footer={null}
              width="100%"
              style={{maxWidth: 520}}
          >
             {selectedRequest && (
                 <div className="text-sm text-slate-700">
                    <p><strong>{t('store.name')}:</strong> {selectedRequest?.name}</p>
                    {
                        selectedRequest?.phone && (
                            <p>
                               <strong>{t('store.phoneNumber')}:</strong>{' '}
                               <a href={`tel:+${selectedRequest?.phone}`} className="text-[#1D2C7B] tabular-nums">
                                  {selectedRequest?.phone}
                               </a>
                            </p>
                        )
                    }
                    {selectedRequest?.category && (
                        <>
                           <p><strong>{t('common.category')}:</strong> {selectedRequest?.category.name}</p>
                           {/*<p><strong>Миқдор:</strong> {selectedRequest?.qty}</p>*/}
                           {/*<p><strong>Нархи:</strong> {selectedRequest?.price}</p>*/}
                        </>
                    )}
                 </div>
             )}
          </Modal>

          {/* Modal for creating a new request */}
          <Modal
              centered
              title={t('store.newRequest')}
              open={isCreateModalVisible}
              onCancel={closeCreateModal}
              onOk={() => form.submit()} // Submit the form when the OK button is clicked
              okText={loadingRequest ? <Spin /> : t('common.create')} // Show loader while loading
              okButtonProps={{ loading: loadingRequest }}
              width="100%"
              style={{maxWidth: 520}}
          >
             <Form form={form} onFinish={handleCreateRequest} layout="vertical">
                <Form.Item
                    label={t('store.warehouseLabel')}
                    name="warehouse_id"
                    rules={[{ required: true, message: t('store.pickProductRequired') }]}
                >
                   <Select
                       allowClear
                       placeholder={t('store.pickProduct')}
                       showSearch
                       onChange={(value) => {
                          const selectedWarehouse = warehouses.find(warehouse => warehouse.id === value);
                          console.log(selectedWarehouse)
                          setSelectedWarehouse(selectedWarehouse); // Update the selected warehouse
                       }}
                       filterOption={(input, option) => {
                          const warehouseName = option.children[0].toLowerCase();
                          return warehouseName.includes(input.toLowerCase());
                       }}
                   >
                      {warehouses?.map(warehouse => (
                          <Select.Option
                              disabled={warehouse?.qty === 0}
                              key={warehouse?.id}
                              value={warehouse?.id}
                          >
                             {warehouse?.name} - <span
                              className="inline-flex gap-3 items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-500/10">
            {t('common.pieces', { count: warehouse.qty })}
          </span>
                          </Select.Option>
                      ))}
                   </Select>
                </Form.Item>

                <Form.Item
                    label={t('common.qty')}
                    name="qty"
                    rules={[
                       {
                          required: true,
                          message: t('store.qtyRequired')
                       },
                       {
                          validator: (_, value) => {
                             if (value === 0) {
                                return Promise.reject(new Error(t('store.qtyZero')));
                             }
                             if (value > selectedWarehouse.qty) {
                                return Promise.reject(new Error(t('store.qtyMaxAvailable', { qty: selectedWarehouse.qty })));
                             }

                             return Promise.resolve();
                          }
                       }
                    ]}
                >
                   <Input
                       type="number"
                       placeholder={t('store.qtyPhInput')}
                   />
                </Form.Item>

             </Form>
          </Modal>

          {/* Modal for full description */}
          <Modal
              title={t('store.fullRemark')}
              open={isDescriptionModalVisible}
              onCancel={closeDescriptionModal}
              footer={null}
              width="100%"
              style={{maxWidth: 520}}
          >
             <p className="text-sm text-slate-700">{fullDescription}</p>
          </Modal>
       </PageShell>
   );
}

export default DoctorRequest;
